type formatDateProps = 'absolute' | 'relative';

function formatDate(isoString: string, format: formatDateProps): string {
  const date = new Date(isoString);
  const now = new Date();

  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 1000 / 60);
  const diffHours = Math.floor(diffMin / 60);

  const sameDay = (d1: Date, d2: Date): boolean =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const hour = date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const formatedDate = date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  if (format === 'absolute') {
    if (sameDay(date, now)) return `Hoje • ${hour}`;
    if (sameDay(date, yesterday)) return `Ontem • ${hour}`;
    return `${formatedDate} • ${hour}`;
  }

  if (format === 'relative') {
    if (diffMin < 1) return 'Agora';
    if (diffMin < 60) return `Há ${diffMin} min`;
    if (sameDay(date, now)) return `Há ${diffHours}h`;
    if (sameDay(date, yesterday)) return `Ontem • ${hour}`;
    return `Último • ${formatedDate}`;
  }

  return formatedDate;
}

const normalizeStr = (str: string): string =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const delegatedAccessLabels: Record<string, string> = {
  pending_consent: 'Consentimento pendente',
  approved: 'Solicita\u00e7\u00e3o aprovada',
  active: 'Sess\u00e3o delegada ativa',
  ended: 'Sess\u00e3o encerrada',
  rejected: 'Solicita\u00e7\u00e3o negada',
  expired: 'Solicita\u00e7\u00e3o expirada',
  canceled: 'Solicita\u00e7\u00e3o cancelada',
  revoked: 'Revogado',
};

const methodVerb: Record<string, string> = {
  GET: 'Consultou',
  POST: 'Criou',
  PUT: 'Atualizou',
  PATCH: 'Atualizou',
  DELETE: 'Removeu',
};

const resourceLabels: Record<string, string> = {
  plans: 'planos',
  credits: 'cr\u00e9ditos',
  companies: 'dados da empresa',
  oversight: 'acesso delegado',
  users: 'usu\u00e1rios',
  notifications: 'notifica\u00e7\u00f5es',
  invoices: 'faturas',
  inspections: 'vistorias',
  samples: 'amostras',
  'work-orders': 'OS',
  pictures: 'fotos',
  rooms: 'c\u00f4modos',
  attachments: 'anexos',
  forms: 'formul\u00e1rios',
};

// Session-related HTTPException subclasses raised by the API (app/base/errors/exceptions.py).
// error_type in event_metadata is always the raw exception class name (type(exc).__name__),
// so unmapped/unknown values fall back to the raw string instead of breaking the render.
const errorTypeLabels: Record<string, string> = {
  HTTP400Exception: 'Requisição inválida',
  HTTP401Exception: 'Sessão inválida',
  HTTP403Exception: 'Acesso não autorizado',
  HTTP404Exception: 'Sessão não encontrada',
  HTTP409Exception: 'Conflito de sessão',
  HTTP500Exception: 'Erro interno',
};

function getErrorTypeTitle(errorType?: string): string {
  if (!errorType) return 'Erro desconhecido';
  return errorTypeLabels[errorType] ?? errorType;
}

const terminatedByLabels: Record<string, string> = {
  'support:user_id': 'Sessão encerrada pelo suporte',
  'system:inactivity': 'Sessão encerrada por inatividade',
  'user:logout': 'Sessão encerrada pelo usuário',
};

function getTerminatedByLabel(terminatedBy?: string): string {
  if (!terminatedBy) return 'Sessão encerrada';
  return terminatedByLabels[terminatedBy] ?? 'Sessão encerrada';
}

function describeSessionEvent(method: string, endpoint: string): string {
  const path = endpoint.toLowerCase();

  if (path.includes('/login') || path.includes('/auth/')) {
    return 'Login realizado';
  }

  if (path.includes('/work-orders')) {
    if (path.includes('/approve')) return 'Aprovou o resultado';
    if (
      path.includes('/reject') ||
      path.includes('/previous-status') ||
      path.includes('/pept')
    ) {
      return 'Recusou o resultado';
    }
    if (path.includes('/report')) return 'Acessou o laudo';
    if (path.includes('/samples')) return 'Consultou as amostras da OS';
    const idMatch = endpoint.match(/work-orders\/(\d+)/);
    if (idMatch) return `Abriu a OS #${idMatch[1]}`;
    if (method === 'POST') return 'Criou uma OS';
    return 'Visualizou a lista de OS';
  }

  if (path.includes('sample')) {
    if (method === 'POST') return 'Criou uma amostra';
    if (method === 'DELETE') return 'Removeu uma amostra';
    if (method === 'PUT' || method === 'PATCH') return 'Atualizou uma amostra';
    return 'Consultou amostras';
  }

  if (path.includes('inspection')) {
    if (method === 'POST') return 'Criou uma vistoria';
    return 'Consultou vistorias';
  }

  const verb = methodVerb[method] ?? 'Acessou';
  const segMatch = endpoint.match(/\/api\/[^/]+\/([^/?]+)/i);
  const key = segMatch ? segMatch[1].toLowerCase() : '';
  const resource =
    resourceLabels[key] ?? (key ? key.replace(/-/g, ' ') : 'um recurso');

  return `${verb} ${resource}`;
}

export {
  delegatedAccessLabels,
  describeSessionEvent,
  formatDate,
  getErrorTypeTitle,
  getTerminatedByLabel,
  normalizeStr,
};
